(function () {
  "use strict";

  if (customElements.get("consent-gate")) return;

  const selectors = {
    checkbox: "[data-consent-checkbox]",
    attributes: "[data-consent-attributes]",
    checkout: "[data-consent-checkout]",
    documentLink: "[data-consent-document-link]",
    documentBody: "[data-consent-document-body]",
    documentError: "[data-consent-document-error]",
  };

  /**
   * Keeps the cart checkout button disabled until the agreement checkbox is ticked and, while it is ticked,
   * adds hidden cart attribute inputs so the order records what was agreed to and when.
   *
   * The theme's cart JS enables every button and input in the cart after each quantity update, so the
   * disabled state is re-applied whenever it is removed while the box is unticked.
   */
  customElements.define(
    "consent-gate",
    class ConsentGate extends HTMLElement {
      connectedCallback() {
        this.form = this.closest("form");
        this.checkbox = this.querySelector(selectors.checkbox);
        this.attributesHolder = this.querySelector(selectors.attributes);
        this.checkoutButton = this.form?.querySelector(selectors.checkout);

        if (!this.form || !this.checkbox || !this.checkoutButton) return;

        this.onChange = () => this.update();
        this.onSubmit = (event) => this.guardSubmit(event);
        this.onClick = (event) => this.openDocument(event);

        this.checkbox.addEventListener("change", this.onChange);
        this.form.addEventListener("submit", this.onSubmit);
        this.addEventListener("click", this.onClick);

        this.observer = new MutationObserver(() => this.syncButton());
        this.observer.observe(this.checkoutButton, { attributes: true, attributeFilter: ["disabled"] });

        this.update();
      }

      disconnectedCallback() {
        this.checkbox?.removeEventListener("change", this.onChange);
        this.form?.removeEventListener("submit", this.onSubmit);
        this.removeEventListener("click", this.onClick);
        this.observer?.disconnect();
      }

      update() {
        this.syncButton();
        this.syncAttributes();
      }

      syncButton() {
        const shouldDisable = !this.checkbox.checked;

        if (this.checkoutButton.disabled !== shouldDisable) {
          this.checkoutButton.disabled = shouldDisable;
        }
      }

      syncAttributes() {
        this.attributesHolder.replaceChildren();

        if (!this.checkbox.checked) return;

        const { attributeTerms, attributeTermsVersion, attributeConsent, attributeConsentVersion, attributeTimestamp } =
          this.dataset;

        this.attributesHolder.append(
          this.createAttributeInput(attributeTerms, attributeTermsVersion),
          this.createAttributeInput(attributeConsent, attributeConsentVersion),
          this.createAttributeInput(attributeTimestamp, new Date().toISOString()),
        );
      }

      createAttributeInput(name, value) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = `attributes[${name}]`;
        input.value = value || "";

        return input;
      }

      guardSubmit(event) {
        // Only the checkout submit leaves the cart. Other submits just update the cart.
        if (event.submitter?.name === "checkout" && !this.checkbox.checked) {
          event.preventDefault();
          this.syncButton();
          this.checkbox.focus();
        }
      }

      openDocument(event) {
        const link = event.target.closest(selectors.documentLink);
        if (!link) return;

        const modal = document.querySelector(`[data-consent-document-modal="${link.dataset.consentDocumentLink}"]`);

        // Without the theme's popup component, fall back to following the link.
        if (!modal || typeof modal.popupOpen !== "function") return;

        event.preventDefault();

        if (!modal.hasAttribute("data-consent-bound")) {
          modal.setAttribute("data-consent-bound", "");

          // The cart drawer closes on any Escape keyup that reaches it, so keep it inside the modal.
          modal.addEventListener("keyup", (keyEvent) => {
            if (keyEvent.code === "Escape") keyEvent.stopPropagation();
          });
        }

        if (window.theme?.a11y) window.theme.a11y.lastElement = link;

        modal.popupOpen();
        this.loadDocument(modal);
      }

      loadDocument(modal) {
        const body = modal.querySelector(selectors.documentBody);
        if (!body || body.hasAttribute("data-loaded") || body.hasAttribute("data-loading")) return;

        body.setAttribute("data-loading", "");

        fetch(body.dataset.url, { credentials: "same-origin" })
          .then((response) => {
            if (!response.ok) throw new Error(`Consent document request failed with ${response.status}`);

            return response.text();
          })
          .then((html) => {
            const page = new DOMParser().parseFromString(html, "text/html");

            // Policy pages render inside .shopify-policy__body, storefront pages inside the main page .rte.
            const content = page.querySelector(".shopify-policy__body") || page.querySelector("#MainContent .rte");
            if (!content) throw new Error("Consent document content not found");

            body.innerHTML = content.innerHTML;
            body.setAttribute("data-loaded", "");
          })
          .catch((error) => {
            console.error(error);

            const errorTemplate = modal.querySelector(selectors.documentError);
            if (errorTemplate) body.replaceChildren(errorTemplate.content.cloneNode(true));
          })
          .finally(() => body.removeAttribute("data-loading"));
      }
    },
  );
})();
